import {
  append,
  arrayIntoChunks,
  excludeNullOrUndefined,
  findAndUpdate,
  getAverage,
  getUnique,
  isInListBy,
  notNullOrUndefined,
  removeFromList,
  toggleInList,
  toggleInListBy,
} from '@akin-home/arrays';
import { getId, isRecordInSet, Record } from '@akin-home/records';

/** Export functions from arrays lib for backwards compatibility */
export {
  append,
  removeFromList,
  getUnique,
  getAverage,
  excludeNullOrUndefined,
  notNullOrUndefined,
  toggleInList,
  isInListBy,
  toggleInListBy,
  arrayIntoChunks,
  isRecordInSet,
  findAndUpdate,
};

export const findAndUpdateRecord = <T extends { id: string }>(
  records: T[],
  record: T
): T[] => {
  // Copy the list
  const listCopy = [...records];
  // Locate the index
  const idx = listCopy.findIndex((i) => i.id === record.id);
  // If not found, just return original
  if (idx < 0) return records;
  // Update item
  listCopy[idx] = record;

  return listCopy;
};

export const filterRecordInSet =
  <T extends Record>(records: T[]) =>
  (record: T) =>
    isRecordInSet(records, record);

export const filterRecordNotInSet =
  <T extends Record>(records: T[]) =>
  (record: T) =>
    !isRecordInSet(records, record);

export const toggleInListById = <T extends { id: number | string }>(
  list: T[],
  item: T
): T[] => {
  if (list.map(getId).includes(item.id as string)) {
    return list.filter((i) => i.id !== item.id);
  } else {
    return [...list, item];
  }
};

export const joinArrayBy = (
  array: any[],
  attr: string,
  delimiter = ', '
): string => array.map((i) => i[attr]).join(delimiter);

/** Adds record if matching on does not exists. Updates existing record if it does exist */
export const addOrReplaceRecord = <T extends { id: string }>(
  records: T[],
  record: T
): T[] =>
  isRecordInSet(records, record)
    ? findAndUpdateRecord(records, record)
    : [...records, record];

export const removeItemFromListByIndex = <T>(list: T[], idx: number): T[] => {
  const updatedList = [...list];
  updatedList.splice(idx, 1);
  return updatedList;
};

export const moveItemFromListByIndexDown = <T>(list: T[], idx: number): T[] => {
  if (idx === 0) {
    return list;
  }

  const updatedList = [...list];
  const b = updatedList[idx];
  updatedList[idx] = updatedList[idx - 1];
  updatedList[idx - 1] = b;

  return updatedList;
};

export const moveItemFromListByIndexUp = <T>(list: T[], idx: number): T[] => {
  if (idx === list.length - 1) {
    return list;
  }

  const updatedList = [...list];
  const b = updatedList[idx];
  updatedList[idx] = updatedList[idx + 1];
  updatedList[idx + 1] = b;

  return updatedList;
};

/** Updates an item in a list based on index */
export const updateItemInListByIndex = <T>(
  list: T[],
  idx: number,
  item: T
): T[] => {
  const updatedList = [...list];
  updatedList[idx] = item;
  return updatedList;
};

/** Removes item from record list field */
export const removeItemFromRecordListField =
  <T>(field: keyof T) =>
  (record: T, idx: number): T => ({
    ...record,
    [field]: removeItemFromListByIndex(record[field] as any, idx),
  });

/** Adds item from record list field */
export const addItemToRecordListField =
  <T, L>(field: keyof T, defaultValue?: L) =>
  (record: T, item?: L): T => ({
    ...record,
    [field]: append(record[field] as any, item || defaultValue),
  });

/** Reorders item from record list field */
export const reorderItemFromRecordListFieldUpwards =
  <T>(field: keyof T) =>
  (record: T, idx: number): T => ({
    ...record,
    [field]: moveItemFromListByIndexUp(record[field] as any, idx),
  });

export const reorderItemFromRecordListFieldDownwards =
  <T>(field: keyof T) =>
  (record: T, idx: number): T => ({
    ...record,
    [field]: moveItemFromListByIndexDown(record[field] as any, idx),
  });

/** Updates item in record list field */
export const updateRecordListField =
  <T, L>(field: keyof T) =>
  (record: T, idx: number, item: L): T => ({
    ...record,
    [field]: updateItemInListByIndex(record[field] as any, idx, item),
  });

type ListFieldFunctions<T, L> = [
  (record: T, item?: L) => T,
  (record: T, idx: number, item: L) => T,
  (record: T, idx: number) => T
];

export const generateListFieldFunctions = <T, L>(
  field: keyof T,
  defaultValue?: L
): ListFieldFunctions<T, L> => [
  addItemToRecordListField(field, defaultValue),
  updateRecordListField(field),
  removeItemFromRecordListField(field),
];

type ListFieldFunctionsOrdered<T, L> = [
  (record: T, item?: L) => T,
  (record: T, idx: number, item: L) => T,
  (record: T, idx: number) => T,
  (record: T, idx: number) => T,
  (record: T, idx: number) => T
];

export const generateListFieldFunctionsReordered = <T, L>(
  field: keyof T,
  defaultValue?: L
): ListFieldFunctionsOrdered<T, L> => [
  addItemToRecordListField(field, defaultValue),
  updateRecordListField(field),
  removeItemFromRecordListField(field),
  reorderItemFromRecordListFieldDownwards(field),
  reorderItemFromRecordListFieldUpwards(field),
];

export const filterByTitle = <T>(
  items: (T & { title: string })[],
  searchStr: string
): T[] => {
  const cleanedSearchStr = searchStr.toLowerCase().trim();
  if (cleanedSearchStr.length === 0) return [];
  let foundItems = items.filter(
    (item) => item.title.toLowerCase() === cleanedSearchStr
  );
  if (!foundItems || foundItems.length === 0) {
    return items.filter((item) =>
      item.title.toLowerCase().includes(cleanedSearchStr)
    );
  } else {
    return foundItems;
  }
};

export const areArrayOfObjsEqual = (
  arrayObj1: any[] | undefined,
  arrayObj2: any[] | undefined,
  byFields?: string[] | undefined
) => {
  if (!arrayObj1 && !arrayObj2) return true;
  if (!arrayObj1 || !arrayObj2) return false;
  if (arrayObj1.length !== arrayObj2.length) return false;
  if (arrayObj1.length === 0) return true;

  if (!byFields || byFields.length === 0) {
    byFields = Array.from(
      new Set([...Object.keys(arrayObj1[0]), ...Object.keys(arrayObj2[0])])
    );
  }

  let arraysEqual = true;
  arrayObj1.forEach((obj, i) => {
    byFields.forEach((byField) => {
      if (obj[byField] !== arrayObj2[i][byField]) {
        arraysEqual = false;
        return false;
      }
    });
  });
  return arraysEqual;
};

export const doStringArraysIntersect = (
  strArr1: string[],
  strArr2: string[]
) => {
  let arraysIntersect = false;
  const cleanedStrArr1 = strArr1.map((str1) => str1.toLowerCase().trim());

  cleanedStrArr1.forEach((str1) => {
    if (strArr2.includes(str1)) {
      arraysIntersect = true;
      return false;
    }
  });

  return arraysIntersect;
};
